import { useRef, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useToast,
  Icon,
  Divider,
} from '@chakra-ui/react'
import { DownloadIcon, InfoOutlineIcon, ViewIcon } from '@chakra-ui/icons'
import { useLazyQuery } from '@apollo/client'
import moment from 'moment'
import { motion } from 'framer-motion'
import { downloadCsv, rowsToCsv } from 'src/lib/csv'
import SchoolExportModal from 'src/components/FullDashboardCell/SchoolExportModal'
import SchoolCsvDownloadMenu from 'src/components/FullDashboardCell/SchoolCsvDownloadMenu'
import {
  SCHOOLS_EXPORT_QUERY,
  EXPORT_COLUMNS,
} from 'src/components/FullDashboardCell/schoolExportShared'

const MotionBox = motion(Box)

const exportFilename = (filter) => {
  const slug = filter?.hasCmsSites
    ? 'cms-sites'
    : String(filter?.package || 'schools')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase()
  return `schools-export-${slug}-${moment().format('YYYY-MM-DD')}.csv`
}

const SchoolListDownloadButton = ({ packages = [] }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const toast = useToast()
  const downloadFilter = useRef(null)

  const [fetchAllSchools, { loading: downloading }] = useLazyQuery(
    SCHOOLS_EXPORT_QUERY,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const filter = downloadFilter.current
        const list = data.schoolsExport || []
        const csv = rowsToCsv(list, EXPORT_COLUMNS)
        downloadCsv(exportFilename(filter), csv)
        const scope = filter?.hasCmsSites
          ? 'with CMS sites'
          : `on the ${filter?.label || filter?.package} package`
        toast({
          title: 'Export ready',
          description: `Downloaded ${list.length} schools ${scope}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: (error) => {
        toast({
          title: 'Export failed',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      },
    }
  )

  const handleDownload = (filter) => {
    downloadFilter.current = filter
    fetchAllSchools({
      variables: {
        hasCmsSites: !!filter?.hasCmsSites,
        package: filter?.package || null,
      },
    })
  }

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        mt={4}
        p={4}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        bg="white"
        boxShadow="sm"
      >
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          gap={4}
        >
          <Flex align="flex-start" gap={3} flex={1}>
            <Flex
              align="center"
              justify="center"
              boxSize={10}
              borderRadius="full"
              bg="teal.50"
              color="teal.600"
              flexShrink={0}
            >
              <DownloadIcon boxSize={5} />
            </Flex>
            <Box>
              <Heading size="sm" fontWeight={500} mb={1}>
                Export school list
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Preview schools, or download a CSV of schools that have CMS
                sites or that use a selected package. Dormant schools are
                excluded.
              </Text>
            </Box>
          </Flex>

          <Flex gap={2} flexShrink={0} direction={{ base: 'column', sm: 'row' }}>
            <Button
              variant="outline"
              colorScheme="teal"
              size="md"
              leftIcon={<ViewIcon />}
              onClick={() => setModalOpen(true)}
            >
              View school list
            </Button>
            <SchoolCsvDownloadMenu
              packages={packages}
              onDownload={handleDownload}
              downloading={downloading}
            />
          </Flex>
        </Flex>

        <Divider my={3} borderColor="gray.100" />

        <Flex align="center" gap={2} color="gray.500" fontSize="xs">
          <Icon as={InfoOutlineIcon} boxSize={3.5} />
          <Text>
            Preview loads 25 schools at a time for faster browsing
          </Text>
        </Flex>
      </MotionBox>

      <SchoolExportModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        packages={packages}
        onDownload={handleDownload}
        downloading={downloading}
      />
    </>
  )
}

export default SchoolListDownloadButton
